/**
 * NoliaHome - Assortiment des accessoires a la taille du produit principal.
 *
 * <nh-size-matcher data-main-size="160x200" data-main-product-id="123" data-rules="...">
 *   ... [data-nh-row data-handle="..."] (une ligne par accessoire) contenant :
 *       <script type="application/json" data-nh-variants>[{id,size,price,compare,available,image}]</script>
 *       [data-nh-price] [data-nh-compare] [data-nh-badge] [data-nh-size] > [data-nh-size-value]
 *       [data-nh-img] (img) [data-nh-variant-target] (recoit data-variant-id) [data-nh-add] (bouton d'ajout)
 * </nh-size-matcher>
 *
 * La logique est identique a snippets/nh-size-match.liquid (rendu initial cote serveur).
 * Regles : voir snippets/nh-size-match-rules.liquid.
 */
import { ThemeEvents, CartUpdatedEvent, ToggleCrossSellsUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/** "140x190 cm" -> "140x190" ; renvoie '' si la valeur n'est pas une taille LxL */
export function normalizeSize(value) {
  const n = String(value ?? '')
    .toLowerCase()
    .replace(/cm/g, '')
    .replace(/\s+/g, '')
    .replace(/×/g, 'x');
  const m = n.match(/^(\d+)x(\d+)$/);
  if (!m || parseInt(m[1], 10) === 0 || parseInt(m[2], 10) === 0) return '';
  return `${parseInt(m[1], 10)}x${parseInt(m[2], 10)}` === n ? n : '';
}

/** Taille normalisee a partir d'une liste de valeurs d'options */
export function sizeFromOptions(options) {
  for (const option of options || []) {
    const size = normalizeSize(option);
    if (size) return size;
  }
  return '';
}

/** Taille cible ('' = masquer) pour un accessoire selon les regles */
export function targetSize(handle, mainSize, rulesText) {
  const width = mainSize.split('x')[0];
  let rule = '';

  String(rulesText || '')
    .split(/\r?\n/)
    .forEach((line) => {
      const l = line.trim();
      const idx = l.indexOf(':');
      if (idx < 1) return;
      if (l.slice(0, idx).trim().toLowerCase() === handle) rule = l.slice(idx + 1);
    });

  let vFull = '';
  let vWidth = '';
  let vStar = '';

  rule.split(',').forEach((entry) => {
    const kv = entry.split('=');
    if (kv.length !== 2) return;
    const clean = (s) => s.trim().toLowerCase().replace(/cm/g, '').replace(/\s+/g, '');
    const k = clean(kv[0]);
    const v = clean(kv[1]);
    if (k === mainSize) vFull = v;
    else if (k === width) vWidth = v;
    else if (k === '*') vStar = v;
  });

  const decision = vFull || vWidth || vStar || 'same';
  if (decision === 'same') return mainSize;
  if (decision === 'none') return '';
  return decision;
}

/** Variante assortie (ou null) */
export function pickVariant(variants, handle, mainSize, rulesText, fallbackId) {
  if (!Array.isArray(variants) || variants.length === 0) return null;

  if (variants.length === 1) return variants[0].available ? variants[0] : null;

  if (!mainSize) {
    const fallback = variants.find((v) => String(v.id) === String(fallbackId)) || variants.find((v) => v.available);
    return fallback && fallback.available ? fallback : null;
  }

  const target = targetSize(handle, mainSize, rulesText);
  if (!target) return null;

  return variants.find((v) => v.available && v.size === target) || null;
}

class NhSizeMatcher extends HTMLElement {
  connectedCallback() {
    this.rules = this.dataset.rules || '';
    this.mainSize = this.dataset.mainSize || '';
    this.rows = Array.from(this.querySelectorAll('[data-nh-row]'));

    this.rows.forEach((row) => {
      try {
        row._nhVariants = JSON.parse(row.querySelector('script[data-nh-variants]')?.textContent || '[]');
      } catch (e) {
        row._nhVariants = [];
      }
      row._nhInitialId = row.dataset.variantId;
    });

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    this.addEventListener('click', this.onClick);

    this.update(this.mainSize, false);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    this.removeEventListener('click', this.onClick);
  }

  onVariantUpdated = (event) => {
    const detail = event.detail || {};
    const productId = detail.data?.productId;
    if (productId && this.dataset.mainProductId && String(productId) !== String(this.dataset.mainProductId)) return;

    const resource = detail.resource;
    if (!resource) return;

    const options = Array.isArray(resource.options) ? resource.options : [];
    let size = sizeFromOptions(options);
    if (!size && resource.title) size = sizeFromOptions(String(resource.title).split('/'));

    this.mainSize = size;
    this.dataset.mainSize = size;
    this.update(size, true);
  };

  update(mainSize, notify) {
    let visibleCount = 0;
    let toggleChanged = false;

    this.rows.forEach((row) => {
      const variant = pickVariant(row._nhVariants, row.dataset.handle, mainSize, this.rules, row._nhInitialId);
      const checkbox = row.querySelector('[data-ref="toggle-cross-sell-checkbox"]');

      if (!variant) {
        if (!row.hidden) toggleChanged = toggleChanged || !!checkbox?.checked;
        row.hidden = true;
        if (checkbox) checkbox.checked = false;
        return;
      }

      visibleCount += 1;
      row.hidden = false;
      if (String(row.dataset.variantId) !== String(variant.id) && checkbox?.checked) toggleChanged = true;
      this.renderRow(row, variant);
    });

    const emptyTarget = this.closest('[data-nh-hide-when-empty]') || this;
    emptyTarget.hidden = visibleCount === 0;

    if (notify && (toggleChanged || this.closest('toggle-cross-sell-list'))) {
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
    }
  }

  renderRow(row, variant) {
    const price = parseInt(variant.price, 10) || 0;
    const compare = parseInt(variant.compare, 10) || 0;
    const onSale = compare > price;

    row.dataset.variantId = variant.id;
    row.querySelectorAll('[data-nh-variant-target]').forEach((el) => (el.dataset.variantId = variant.id));

    // Prix utilises par toggle-cross-sell.js (getPrices) pour le total du bouton
    if (row.tagName.toLowerCase() === 'toggle-cross-sell') {
      row.dataset.price = price;
      if (onSale) row.dataset.compareAtPrice = compare;
      else delete row.dataset.compareAtPrice;
    }

    const priceEl = row.querySelector('[data-nh-price]');
    const compareEl = row.querySelector('[data-nh-compare]');
    const badgeEl = row.querySelector('[data-nh-badge]');
    const sizeEl = row.querySelector('[data-nh-size]');
    const sizeValueEl = row.querySelector('[data-nh-size-value]');
    const imgEl = row.querySelector('[data-nh-img]');

    if (priceEl) priceEl.textContent = formatMoney(price);
    if (compareEl) {
      compareEl.textContent = onSale ? formatMoney(compare) : '';
      compareEl.hidden = !onSale;
    }
    if (badgeEl) {
      badgeEl.textContent = onSale ? `- ${formatMoney(compare - price)}` : '';
      badgeEl.hidden = !onSale;
    }
    if (sizeEl) {
      sizeEl.hidden = !variant.size;
      if (sizeValueEl) sizeValueEl.textContent = variant.size ? `${variant.size} cm` : '';
    }
    if (imgEl && variant.image && imgEl.getAttribute('src') !== variant.image) {
      imgEl.src = variant.image;
      imgEl.removeAttribute('srcset');
    }
  }

  onClick = (event) => {
    const button = event.target.closest('[data-nh-add]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();

    const row = button.closest('[data-nh-row]');
    const id = row?.dataset.variantId;
    if (!id || row.hidden || button.classList.contains('is-loading')) return;

    this.addToCart(button, id);
  };

  async addToCart(button, id) {
    button.classList.add('is-loading');
    button.disabled = true;

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }], sections: sections.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Erreur');
      }

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      const cartIcon = document.querySelector('cart-icon');
      const currentItemCount = cartIcon?.getCurrentItemCount?.();
      const itemCount = currentItemCount != null ? currentItemCount + 1 : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount, source: 'nh-complete-room', sections: data.sections }),
      );

      if (cartIcon?.addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: Theme.translations?.addToCartNotificationMessage },
          }),
        );
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 2000);
    } catch (error) {
      console.error('nh-complete-room:', error);
      button.classList.remove('is-loading');
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('nh-size-matcher')) {
  customElements.define('nh-size-matcher', NhSizeMatcher);
}
